import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { cva } from 'class-variance-authority';
import { cn, getInitials, hashStringToHsl } from '@/lib/utils';
const avatarVariants = cva([
    'relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full',
    'font-display font-bold text-white',
    'border-2 border-ink-700 ring-0 transition-shadow duration-200',
], {
    variants: {
        size: {
            xs: 'h-6  w-6  text-[0.6rem]',
            sm: 'h-8  w-8  text-xs',
            md: 'h-10 w-10 text-sm',
            lg: 'h-14 w-14 text-base',
            xl: 'h-20 w-20 text-xl',
            '2xl': 'h-28 w-28 text-2xl',
        },
        border: {
            none: 'border-transparent',
            bronze: 'border-bronze',
            silver: 'border-silver',
            gold: 'border-gold ring-2 ring-gold/30',
            electric: 'border-electric-400',
            royal: 'border-royal-400',
        },
    },
    defaultVariants: { size: 'md', border: 'none' },
});
/**
 * Avatar joueur. Si `src` est fourni, affiche l'image. Sinon, fallback SVG :
 * initiales sur un fond HSL déterministe à partir du pseudo.
 */
export const Avatar = forwardRef(function Avatar({ className, size, border, pseudo, src, borderColor, style: extraStyle, ...props }, ref) {
    const initials = getInitials(pseudo);
    const bg = hashStringToHsl(pseudo);
    // Quand une couleur custom est fournie, on neutralise la prop `border`
    // (sinon les classes Tailwind l'emportent).
    const variantClass = avatarVariants({ size, border: borderColor ? 'none' : border });
    const style = {
        ...(src ? {} : { backgroundColor: bg }),
        ...(borderColor ? { borderColor } : {}),
        ...(extraStyle ?? {}),
    };
    return (_jsx("span", { ref: ref, className: cn(variantClass, borderColor && 'border-2', className), style: style, "aria-label": `Avatar de ${pseudo}`, ...props, children: src ? (_jsx("img", { src: src, alt: pseudo, className: "h-full w-full object-cover" })) : (_jsx("span", { className: "drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]", children: initials })) }));
});
